import { TriCheckbox } from "@nova/component-checkbox"
import { Dropdown } from "@nova/component-dropdown"
import * as Icons from "@nova/component-icons"
import { ScrollableArea } from "@nova/component-scrollable-area"
import classNames from "classnames"
import React, { useCallback, useRef, useState } from "react"

type SelectOptionGroup<T, S> = {
    sectionTitle?: string
    options: readonly SelectOption<T, S>[]
}

type SelectOption<T, S> = {
    label: string | React.ReactNode
    searchableText?: string
    value: T
    disabled?: boolean
    multiSubConfig?: {
        options: SelectOption<S, S>[]
        selected: SelectOption<S, S>[]
        onChange: (value: S) => void
    }
}

/** A flat option or a grouped section — mix freely in the same array. */
type SelectItem<T, S> = SelectOption<T, S> | SelectOptionGroup<T, S>

/** Convenience alias for an array of grouped sections. */
type GroupedSelectOptions<T, S> = readonly SelectOptionGroup<T, S>[]

type SelectProps<T, S> = {
    options: readonly SelectItem<T, S>[]
    value: T
    defaultValue?: T
    onChange: (value: T) => void
    getOptionValue?: (value: T) => string | number | undefined
    placeholder?: string
    disabled?: boolean
    getTitle?: (v: T) => string
    className?: string
    buttonClassName?: string
    searchable?: boolean
    defaultOpen?: boolean
}

function isGroup<T, S>(item: SelectItem<T, S>): item is SelectOptionGroup<T, S> {
    return "options" in item
}

function BaseSelect<T, S = never>({
    options,
    value,
    defaultValue,
    onChange,
    getOptionValue = (v) => String(v),
    placeholder = "Select an option",
    disabled,
    getTitle,
    className,
    buttonClassName,
    searchable,
    defaultOpen,
}: SelectProps<T, S>) {
    const [search, setSearch] = useState("")
    const [isOpen, setIsOpen] = useState(false)
    const searchInputRef = useRef<HTMLInputElement>(null)

    const handleClose = useCallback(() => setSearch(""), [])

    const flatOptions = options.flatMap((item) => (isGroup(item) ? item.options : [item]))

    const resolvedValue = value ?? defaultValue
    const current =
        resolvedValue == null
            ? undefined
            : flatOptions.find((opt) => getOptionValue(opt.value) === getOptionValue(resolvedValue))

    // Resolved string label for use as input placeholder when searchable
    const selectedLabel = getTitle
        ? getTitle(value)
        : typeof current?.label === "string"
          ? current.label
          : undefined

    const matchesSearch = (opt: SelectOption<T, S>) => {
        const text = opt.searchableText ?? (typeof opt.label === "string" ? opt.label : "")
        return text.toLowerCase().includes(search.toLowerCase())
    }

    const filteredItems: readonly SelectItem<T, S>[] = searchable
        ? options
              .map((item) =>
                  isGroup(item) ? { ...item, options: item.options.filter(matchesSearch) } : item
              )
              .filter((item) => (isGroup(item) ? item.options.length > 0 : matchesSearch(item)))
        : options

    const hasResults = filteredItems.length > 0

    return (
        <Dropdown onClose={handleClose} onOpenChange={setIsOpen} defaultOpen={defaultOpen}>
            <div className={classNames("block min-w-[12rem]", className)}>
                <Dropdown.Trigger
                    as={searchable ? "div" : "button"}
                    {...(!searchable && { disabled })}
                    aria-haspopup="listbox"
                    className={classNames(
                        "group justify-between border pl-4 py-2 pr-2 text-left w-full rounded-lg aria-expanded:border-blue-200",
                        buttonClassName,
                        { "opacity-50 cursor-not-allowed": disabled }
                    )}
                >
                    {searchable && isOpen ? (
                        <input
                            ref={searchInputRef}
                            autoFocus
                            className="bg-transparent outline-none focus:ring-0 border-0 p-0 text-gray-0 w-full min-w-0"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={selectedLabel ?? placeholder}
                            onClick={(e) => e.stopPropagation()}
                        />
                    ) : (
                        <span className="text-gray-0">
                            {(getTitle ? getTitle(value) : current?.label) || placeholder}
                        </span>
                    )}
                    <Icons.ArrowDropUp className="text-gray-300 hidden group-aria-expanded:block" />
                    <Icons.ArrowDropdown className="text-gray-300 group-aria-expanded:hidden" />
                </Dropdown.Trigger>
            </div>
            <Dropdown.Menu
                unstyled
                matchTriggerWidth
                className="shadow-lg bg-gray-700 rounded-[6px]"
            >
                <ScrollableArea className="max-h-60 py-1 rounded-[6px] text-gray-0">
                    {hasResults ? (
                        filteredItems.map((item, index) => {
                            if (isGroup(item)) {
                                return (
                                    <React.Fragment key={index}>
                                        {index > 0 && (
                                            <div className="border-t border-gray-600 my-1 mx-2" />
                                        )}
                                        {item.sectionTitle && (
                                            <div className="px-4 pt-2 pb-1 text-xs text-gray-400 uppercase tracking-wider">
                                                {item.sectionTitle}
                                            </div>
                                        )}
                                        {item.options.map((opt) => (
                                            <SelectOptionItem<T, S>
                                                onChange={onChange}
                                                option={opt}
                                                key={String(getOptionValue(opt.value))}
                                                value={value}
                                                getOptionValue={getOptionValue}
                                            />
                                        ))}
                                    </React.Fragment>
                                )
                            }
                            return (
                                <SelectOptionItem<T, S>
                                    onChange={onChange}
                                    option={item}
                                    key={String(getOptionValue(item.value))}
                                    value={value}
                                    getOptionValue={getOptionValue}
                                />
                            )
                        })
                    ) : (
                        <div className="px-4 py-3 text-center">
                            <h5 className="text-gray-400">No results</h5>
                        </div>
                    )}
                </ScrollableArea>
            </Dropdown.Menu>
        </Dropdown>
    )
}

export function Select<T, S = never>(props: SelectProps<T, S>) {
    return <BaseSelect {...props} buttonClassName="bg-gray-700 border-transparent" />
}

export function SelectOutlined<T, S = never>(props: SelectProps<T, S>) {
    return <BaseSelect {...props} buttonClassName="bg-transparent border-gray-600" />
}

interface SelectOptionItemProps<T, S> {
    option: SelectOption<T, S>
    onChange: (val: T) => void
    value: T | null
    getOptionValue?: (value: T) => string | number | undefined
}

const MultiSelectOption = <T, S>({ option }: { option: SelectOption<T, S> }) => {
    const [submenuRect, setSubmenuRect] = useState<DOMRect | null>(null)
    const itemRef = useRef<HTMLDivElement>(null)

    const showSubmenu = () => {
        if (itemRef.current) setSubmenuRect(itemRef.current.getBoundingClientRect())
    }
    const hideSubmenu = () => setSubmenuRect(null)

    return (
        <Dropdown.Item
            ref={itemRef}
            clickBehaviour="keep-open"
            onMouseEnter={showSubmenu}
            onMouseLeave={hideSubmenu}
        >
            <div className="px-4 py-1.5 hover:bg-gray-500 cursor-pointer flex justify-between items-center">
                <span>{option.label}</span>
                <Icons.SmallIcon Icon={Icons.ChevronRight} />
            </div>
            {submenuRect && (
                <div
                    className="fixed pl-1 z-[100]"
                    style={{
                        left: submenuRect.right,
                        top: submenuRect.top,
                        width: submenuRect.width,
                    }}
                >
                    <div className="shadow-lg bg-gray-700 rounded-[6px]">
                        <ScrollableArea className="flex flex-col max-h-60 p-1 rounded-[6px]">
                            {option.multiSubConfig?.options.map((subOpt) => (
                                <div
                                    key={String(subOpt.value)}
                                    className="flex flex-row py-1 gap-3"
                                >
                                    <TriCheckbox
                                        state={
                                            !!option.multiSubConfig?.selected
                                                .map((v) => v.label)
                                                .includes(subOpt.label)
                                                ? "checked"
                                                : "unchecked"
                                        }
                                        onClick={() =>
                                            option.multiSubConfig?.onChange(subOpt.value)
                                        }
                                    />
                                    <span>{subOpt.label}</span>
                                </div>
                            ))}
                        </ScrollableArea>
                    </div>
                </div>
            )}
        </Dropdown.Item>
    )
}

const SelectOptionItem = <T, S>({
    onChange,
    option,
    value,
    getOptionValue,
}: SelectOptionItemProps<T, S>) => {
    if (!option.multiSubConfig) {
        const isSelected = getOptionValue
            ? getOptionValue(option.value) === getOptionValue(value as T)
            : option.value === value
        const isDisabled = !!option.disabled
        return (
            <Dropdown.Item
                clickBehaviour={isDisabled ? "keep-open" : "close"}
                aria-selected={isSelected}
                aria-disabled={isDisabled}
                onClick={() => !isDisabled && onChange(option.value)}
                className={classNames("px-4 py-1.5 rounded-[6px] text-gray-0", {
                    "hover:bg-gray-500 aria-selected:bg-gray-500 cursor-pointer": !isDisabled,
                    "cursor-not-allowed": isDisabled,
                })}
            >
                {typeof option.label === "string" ? <h5>{option.label}</h5> : option.label}
            </Dropdown.Item>
        )
    }
    return <MultiSelectOption option={option} />
}

export type { SelectOption, SelectOptionGroup, SelectItem, GroupedSelectOptions }
